// @ts-strict-ignore
import React, { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Sheet, toast } from '@node-space/ui-components'
import { useCustomerMutation } from 'hooks/mutations/Customers/useCustomerMutation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { BaseErrorResponse } from 'types/beneficiaries'
import { AddCustomerPayload, AddCustomerRequest } from 'types/customers'
import { logSentryErrorAndGetTraceId } from 'utils/sentry'
import { flattenErrorObject } from 'utils/utils'
import { NESTED_CUSTOMERS_ERROR_MAPPING } from '../../constants'
import { ModalStep } from '../../types'
import { getCustomerFields } from '../../utils'
import { AddCustomerStep } from './AddCustomerStep'

interface AddCustomerProps {
  handleCloseSheet: () => void
  handleOnSuccessCloseSheet: () => void
  isSheetOpen: boolean
}

export const AddCustomer = ({
  isSheetOpen,
  handleCloseSheet,
  handleOnSuccessCloseSheet,
}: AddCustomerProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [modalStep, setModalStep] = useState<ModalStep>(ModalStep.ADD_CUSTOMER)
  const [customerFormState, setCustomerFormState] = useState<AddCustomerPayload>(
    {} as AddCustomerPayload
  )
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const { mutate: submitCustomer, isPending: isLoading } = useCustomerMutation<AddCustomerRequest>()
  const [formErrors, setFormErrors] = useState<Record<string, string[]>>({})

  const modalHeader = useMemo(() => {
    if (modalStep === ModalStep.CONFIRMATION) {
      return t('customers.confirmDetails')
    }

    return t('customers.newCustomer')
  }, [modalStep])

  const onBackHandler = () => {
    switch (modalStep) {
      case ModalStep.ADD_CUSTOMER:
        handleCloseSheet()
        break
      case ModalStep.CONFIRMATION:
        requestError && resetRequestError()
        setModalStep(ModalStep.ADD_CUSTOMER)
        break
      default:
        break
    }
  }

  const submit = () => {
    const customer = getCustomerFields(customerFormState)

    submitCustomer(customer, {
      onSuccess: () => {
        handleOnSuccessCloseSheet()
        toast({ header: t('customers.addCustomer.success'), variant: 'success' })
        queryClient.invalidateQueries({ queryKey: ['customers'] })
      },
      onError: (error: BaseErrorResponse) => {
        toast({ header: t('customers.addCustomer.error'), variant: 'error' })

        const errors = error?.data?.details?.errors as unknown
        const flatErrors = flattenErrorObject(errors)
        const allowedKeys = Object.keys(NESTED_CUSTOMERS_ERROR_MAPPING)
        const filteredEntries = Object.entries(flatErrors).filter(([key]) =>
          allowedKeys.includes(key)
        )
        const filteredErrors: Record<string, string[]> | null =
          filteredEntries.length > 0
            ? (Object.fromEntries(filteredEntries) as Record<string, string[]>)
            : null

        const sentryTraceId = filteredErrors
          ? null
          : logSentryErrorAndGetTraceId(
              `Failed API request to /platform/v1/customers with status ${error?.status} - useCustomerMutation`,
              error,
              {
                data: customer,
              }
            )

        if (filteredErrors) {
          setFormErrors(filteredErrors)
          onBackHandler()
        }

        setRequestError({
          show: !filteredErrors,
          errorCode: error.status,
          sentryTraceId,
          ErrorMessages: filteredErrors,
        })
      },
    })
  }

  const appendCustomerDetails = async (customerDetails: AddCustomerPayload) => {
    setCustomerFormState({ ...customerFormState, ...customerDetails })
    setModalStep(ModalStep.CONFIRMATION)
  }

  const resetForm = () => {
    setCustomerFormState({} as AddCustomerPayload)
    setModalStep(ModalStep.ADD_CUSTOMER)
    setFormErrors({})
    requestError && resetRequestError()
  }

  const handleOnOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm()
    }
  }
  return (
    <Sheet
      isOpen={isSheetOpen}
      header={modalHeader}
      onBack={onBackHandler}
      onClose={handleCloseSheet}
      onOpenChange={handleOnOpenChange}
    >
      <AddCustomerStep
        modalStep={modalStep}
        customerFormState={customerFormState}
        onBackHandler={onBackHandler}
        submit={submit}
        appendCustomerDetails={appendCustomerDetails}
        isLoading={isLoading}
        requestError={requestError}
        resetRequestError={resetRequestError}
        formErrors={formErrors}
      />
    </Sheet>
  )
}
